<script lang="ts" setup>
import type { Skill } from '@prisma/client'
import { Iconify } from '~/types/assets'
import { useLocaleThemePath } from '~/composables/locale'

const { skill, openOnMount } = defineProps<{
  skill: Skill
  openOnMount?: boolean
}>()

const emits = defineEmits<{ (e: 'onExpand', status: boolean): void }>()
const skillEl: Ref<HTMLDivElement | null> = ref(null)

const active = ref(false)

const activeModel = computed({
  get: (): boolean => active.value,
  set(status: boolean): void {
    active.value = status
    emits('onExpand', status)
  }
})

function onClose(e: Event) {
  /* prevents outer click to be triggered */
  e.stopPropagation()
  activeModel.value = false
}

function onClickOutside() {
  if (activeModel.value) {
    activeModel.value = false
  }
}

onMounted(() => {
  if (openOnMount) {
    setTimeout(() => {
      skillEl.value?.scrollIntoView({ block: 'end', behavior: 'smooth' })
      activeModel.value = true
    }, 500)
  }
})
</script>

<template lang="pug">
// Atropos(
//   :rotate="true"
//   :active-offset="40"
//   :shadow-scale="1.05"
// )
   NuxtLink(
     :to="useLocaleThemePath(`/skills/${skill?.slug}`)"
     :title="$t('skills.card.anchor.title', { name: skill?.name })"
     :aria-label="`${skill?.name} ${$t('skills.card.anchor.aria-label')}`"
   )
.skill-card__outer(
  @click="activeModel = true",
  ref="skillEl"
  v-click-outside="onClickOutside"
)
  .skill-card__inner(
    :class="activeModel ? 'flex-row bg-dark-950 w-[300px] md:w-[400px] z-50 drop-shadow-light translate-x-[-25%] md:translate-x-[-30%]' : 'flex-col bg-[rgba(0,0,0,0.5)] w-full'"
  )
    .skill-card__head
      .knob-wrapper(:class="activeModel ? 'knob-active': ''")
        UIKnob(
          :min="0"
          :max="100"
          :stroke-width="8"
          :value="skill.percentage"
          :size="100"
        )
      p(v-if="activeModel") # {{ skill.category }}
    .skill-card__body
      NuxtLink.open(
        v-if="activeModel"
        :to="`${useLocaleThemePath?.(undefined, { skill: skill.slug })}`"
      )
        Icon(:name="Iconify?.open")
      span.close(v-if="activeModel" @click="onClose")
        Icon(:name="Iconify.close")
      p.name(:class="activeModel ? 'text-secondary' : 'text-white'") {{ skill.name }}
      .skill-card__body__icon
        Icon(:name="skill.src")
      Transition(:name="activeModel ? 'fade-right' : ''")
        p.description(v-if="activeModel") {{ skill.description }}
    .skill-card__details(
      v-if="activeModel"
    )
    .skill-card__footer(v-if="!activeModel")
      p # {{ skill.category }}
</template>

<style lang="sass" scoped>
.drop-shadow-light
  filter: drop-shadow(0 25px 25px rgb(255 255 255 / 0.25))
.knob-active
  transform: rotate(1440deg)
.skill-card__outer
  @apply w-[150px] h-[240px] relative overflow-visible
  .skill-card__inner
    @apply absolute top-0 bottom-0 flex border border-primary rounded-md cursor-pointer flex-shrink-0 overflow-hidden
    transition: all 1.5s
    // &:hover
    //   @apply scale-90
    .skill-card__head
      @apply md:w-full flex-col flex-center p-2 gap-4
      .knob-wrapper
        transition: all 2s
      p
        @apply text-white
    .skill-card__body
      @apply w-full flex flex-col items-center justify-center gap-2 p-2 pb-4 relative
      p
        &.name
          @apply whitespace-nowrap font-semibold
        &.description
          @apply text-white max-h-[150px] overflow-auto scrollbar-primary text-xs
      a.open
        @apply absolute top-1 left-1
      span.close
        @apply absolute top-1 right-1
      span.iconify
        @apply bg-primary text-2xl p-0 m-0
      .skill-card__body__icon
        @apply w-full flex justify-center relative
        &::before,
        &::after
          content: ''
          @apply border border-primary w-6 absolute top-[50%] translate-y-[-50%]
        &::before
          @apply left-0
        &::after
          @apply right-0
        span.iconify
          @apply border-x border-primary flex-shrink-0 flex items-center px-0.5 bg-secondary
    .skill-card__footer
      @apply bg-primary-o-75 border-y-2 border-primary flex justify-center p-0.5
      p
        @apply text-dark-900 text-sm font-bold whitespace-nowrap

@media screen and (min-width: 768px)
  .knob-active
    transform: rotate(1440deg) scale(150%)
</style>
